/**
 * Mobile view (below `lg`) of the bulk-action bar, rendered by `./ActionBar.tsx`: the selected
 * count on the left and a "More Tools" popover holding the items on the right. Lists
 * `mobileActionItems` when given, otherwise `actionItems`. Hidden when printing.
 *
 * Differences from `./ActionBarDesktop.tsx` a change must know about:
 * - There is no "Unselect All" label; tapping the "Selected N products" text clears the selection.
 *   Quote checkout adds an explicit "Unselect All" item to `mobileActionItems` instead.
 * - `isDisabled` on an item has no effect here: the item renders and its `onClick` fires.
 * - The forwarded ref is the `Popover`'s handle, used by callers to close the menu.
 */
import React, { useState } from "react";
import { Flex, FlexItem } from "@ryerson/frontend.layout";
import styled from "@emotion/styled";
import { css } from "@emotion/react";
import { Typography } from "@ryerson/frontend.typography";
import { Icon, IconList } from "@ryerson/frontend.assets";
import { useTheme } from "@ryerson/frontend.theme";
import { Popover, PopperExposedHandlers } from "@ryerson/frontend.modal";
import { ActionBarProps } from "./ActionBarTypes";
import { ActionBarContentProps } from "./ActionBarContent";

type ActionBarMobileProps = ActionBarProps & {
	content: ActionBarContentProps;
};

const menuWrapper = (props: any) => css`
	background-color: ${props.background};
	padding: 20px;
	box-shadow:
		rgba(0, 0, 0, 0.1) 0px 10px 15px -3px,
		rgba(0, 0, 0, 0.05) 0px 4px 6px -2px;
	@media only screen and (max-width: ${props.breakpoint}) {
		margin-left: -150px;
	}
`;

const menuItem = css`
	&:not(:last-child) {
		padding-bottom: 20px;
	}
`;

const ActionBarMobileContainer = styled(Flex)`
	padding: 20px 10px;
	@media print {
		display: none;
	}
`;

const ActionBarMobileInner: React.ForwardRefRenderFunction<
	PopperExposedHandlers,
	ActionBarMobileProps
> = (
	{
		selectedRows,
		setSelectedRows,
		actionItems,
		mobileActionItems,
		preContent,
		mobileContainerCss: containerCss,
		content,
	},
	ref
) => {
	const { theme } = useTheme();
	const { darkGray, lightGray, white, primaryBrand } = theme.colors.primary;
	const [showMoreTool, setShowMoreTool] = useState<boolean>(false);

	const items = mobileActionItems ?? actionItems;

	const getTriggerSection = (): JSX.Element => (
		<Flex alignItems="center">
			<Typography size="md" css={{ marginRight: "10px" }} color={white}>
				{content.moreTools}
			</Typography>
			<Icon
				icon={showMoreTool ? "chevron-up" : "chevron-down"}
				size="md"
				color={theme.colors.tertiary.header}
				selected={true}
				css={{ marginRight: "10px", flexShrink: 0 }}
				circled={true}
			/>
		</Flex>
	);

	const getMenuSection = (): JSX.Element => (
		<Flex
			style={menuWrapper({
				breakpoint: theme.breakpoints.lg,
				background: theme.colors.primary.background,
			})}
			direction="column"
		>
			{items.map((item) => (
				<Flex key={item.testId} css={menuItem}>
					{"renderJSX" in item ? (
						item.renderJSX()
					) : (
						<Flex
							onClick={item.onClick}
							alignItems="center"
							css={{ cursor: "pointer" }}
							dataTestId={`action-bar-${item.testId}-button-mobile`}
						>
							<Icon
								size="sm"
								icon={item.icon as keyof IconList}
								circled={item.isIconCircled}
								color={primaryBrand}
								css={{ flexShrink: 0, marginRight: "20px" }}
							/>
							<Typography variant="span" color={primaryBrand} size="md">
								{item.title}
							</Typography>
						</Flex>
					)}
				</Flex>
			))}
		</Flex>
	);

	return (
		<>
			{preContent}
			<ActionBarMobileContainer
				css={[
					css`
						background: ${darkGray};
					`,
					containerCss,
				]}
				justifyContent="space-between"
				alignItems="flex-end"
				dataTestId="action-bar-container"
			>
				<FlexItem>
					{selectedRows.length > 0 && (
						<Flex onClick={() => setSelectedRows([])} alignItems="center">
							<Typography type="tertiary" size="md" variant="div">
								{content.selected} &nbsp;
								<Typography weight="bold" color={white}>
									{selectedRows.length}
								</Typography>
								&nbsp;
								{selectedRows.length > 1 ? content.products : content.product}
							</Typography>
						</Flex>
					)}
				</FlexItem>
				<FlexItem>
					<Popover
						ref={ref}
						offset={[0, 10]}
						trigger={getTriggerSection()}
						onClose={() => setShowMoreTool(false)}
						onOpen={() => setShowMoreTool(true)}
						dataTestId="action-bar-mobile-more-tools-popover"
					>
						{getMenuSection()}
					</Popover>
				</FlexItem>
			</ActionBarMobileContainer>
		</>
	);
};

/** Mobile bulk-action bar; the ref controls its "More Tools" popover. */
const ActionBarMobile = React.forwardRef(ActionBarMobileInner);

export default ActionBarMobile;
